import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { passwordLogin } from "../api/auth";
import {
  clearAuth,
  clearOriginalAuth,
  isAdminRole,
  isHrRole,
  loadAuth,
  loadOriginalAuth,
  saveAuth,
  SESSION_EXPIRED_EVENT,
  type StoredAuth,
} from "./authStorage";

import { useQueryClient } from "@tanstack/react-query";
import { revokeSession } from "../api/client";

interface AuthContextValue {
  auth: StoredAuth | null;
  originalAuth: StoredAuth | null;
  /**
   * Authenticate. `remember` defaults to true (localStorage); pass false
   * to scope the session to the current tab via sessionStorage.
   */
  login: (
    identifier: string,
    password: string,
    remember?: boolean,
  ) => Promise<void>;
  completeAuthHandoff: (stored: StoredAuth) => void;
  logout: () => void;
  switchUser: (userId: string) => Promise<void>;
  resetToOriginalUser: () => Promise<void>;
  isHr: boolean;
  isAdmin: boolean;
  isImpersonating: boolean;
  userName: string;
  userRole: string;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [auth, setAuth] = useState<StoredAuth | null>(() => loadAuth());
  const [originalAuth, setOriginalAuth] = useState<StoredAuth | null>(() =>
    loadOriginalAuth(),
  );

  const applyAuth = useCallback((stored: StoredAuth, remember = true) => {
    queryClient.clear();
    saveAuth(stored, remember);
    clearOriginalAuth();
    setAuth(stored);
    setOriginalAuth(null);
  }, [queryClient]);

  const login = useCallback(
    async (identifier: string, password: string, remember = true) => {
      const response = await passwordLogin({ identifier, password });
      const stored: StoredAuth = {
        user: response.user,
        account: response.account,
      };
      applyAuth(stored, remember);
    },
    [applyAuth],
  );

  const completeAuthHandoff = useCallback(
    (stored: StoredAuth) => {
      applyAuth(stored, true);
    },
    [applyAuth],
  );

  const clearLocalSession = useCallback(() => {
    clearAuth();
    queryClient.clear();
    setAuth(null);
    setOriginalAuth(null);
  }, [queryClient]);

  useEffect(() => {
    window.addEventListener(SESSION_EXPIRED_EVENT, clearLocalSession);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, clearLocalSession);
  }, [clearLocalSession]);

  const logout = useCallback(() => {
    const token = loadAuth()?.user.session_jwt;
    clearLocalSession();
    if (token) void revokeSession(token).catch(() => {});
  }, [clearLocalSession]);

  const switchUser = useCallback(async (userId: string) => {
    if (loadAuth()?.user.id !== userId.trim()) {
      throw new Error("Sign in as the other user to switch identities.");
    }
  }, []);

  const resetToOriginalUser = useCallback(async () => {
    const original = loadOriginalAuth();
    if (original && original.user.id !== loadAuth()?.user.id) {
      throw new Error("Sign in as the original user to switch identities.");
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      auth,
      originalAuth,
      login,
      completeAuthHandoff,
      logout,
      switchUser,
      resetToOriginalUser,
      isHr: isHrRole(auth?.user.role),
      isAdmin: isAdminRole(auth?.user.role),
      isImpersonating: Boolean(
        originalAuth && auth && originalAuth.user.id !== auth.user.id,
      ),
      userName: auth?.user.name ?? auth?.user.email ?? "",
      userRole: auth?.user.role ?? "",
    }),
    [
      auth,
      originalAuth,
      login,
      completeAuthHandoff,
      logout,
      switchUser,
      resetToOriginalUser,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
